import type { ReactNode } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { BreadcrumbSegment } from '@/types/nav';
import { Breadcrumb } from '@/components/page/Breadcrumb';
import { cn } from '@/lib/cn';

interface PageTemplateProps {
  title: string;
  description?: string;
  breadcrumb?: BreadcrumbSegment[];
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function PageTemplate({
  title,
  description,
  breadcrumb,
  actions,
  children,
  className,
}: PageTemplateProps) {
  const shell = useOutletContext<{ breadcrumb?: BreadcrumbSegment[] }>();
  const segments = breadcrumb ?? shell?.breadcrumb ?? [];

  return (
    <div className={cn('space-y-3 sm:space-y-6', className)}>
      {/* Breadcrumb */}
      {segments.length > 0 && (
        <nav aria-label="Breadcrumb" className="hidden items-center gap-2 text-sm text-[var(--text-muted)] sm:flex">
          <Breadcrumb segments={segments} />
        </nav>
      )}

      {/* Page Header */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-[var(--text-primary)] truncate sm:text-heading-xl">
            {title}
          </h1>
          {description && (
            <p className="mt-0.5 text-xs text-[var(--text-secondary)] sm:mt-1 sm:text-body-sm">{description}</p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {actions}
          </div>
        )}
      </header>

      {/* Content */}
      <main id="main-content" className="space-y-3 sm:space-y-6">
        {children}
      </main>
    </div>
  );
}

export interface SectionHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  className?: string;
}

export function SectionHeader({ title, description, actions, className }: SectionHeaderProps) {
  return (
    <header className={cn('flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4', className)}>
      <div>
        <h2 className="text-heading-md font-semibold text-[var(--text-primary)]">{title}</h2>
        {description && (
          <p className="mt-1 text-body-sm text-[var(--text-secondary)]">{description}</p>
        )}
      </div>
      {actions && (
        <div className="flex items-center gap-2 flex-shrink-0">
          {actions}
        </div>
      )}
    </header>
  );
}